import React from 'react';
import { DraftListItem as DraftListItemType } from '../../services/draftService';

interface DraftListItemProps {
  draft: DraftListItemType;
  onSelect: (draftId: string) => void;
  onDelete: (draftId: string) => void;
}

const MAX_DESCRIPTION_LENGTH = 100;
const MAX_DATES_SHOWN = 3;

/**
 * Strip HTML tags from a string for plain text display
 */
function stripHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}

/**
 * Single draft item in the drafts list
 */
export const DraftListItem: React.FC<DraftListItemProps> = ({
  draft,
  onSelect,
  onDelete,
}) => {
  const plainDescription = stripHtml(draft.description);
  const truncatedDescription = plainDescription.length > MAX_DESCRIPTION_LENGTH
    ? `${plainDescription.slice(0, MAX_DESCRIPTION_LENGTH)}...`
    : plainDescription;

  const displayDates = draft.dates.slice(0, MAX_DATES_SHOWN);
  const hasMoreDates = draft.dates.length > MAX_DATES_SHOWN;

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(draft.id);
  };

  return (
    <div
      onClick={() => onSelect(draft.id)}
      className="flex gap-4 p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-gray-50 cursor-pointer transition-colors group"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onSelect(draft.id);
        }
      }}
    >
      {/* Thumbnail */}
      <div className="flex-shrink-0 w-20 h-20 rounded-md overflow-hidden bg-gray-100">
        {draft.imageUrl ? (
          <img
            src={draft.imageUrl}
            alt={draft.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <svg
              className="w-8 h-8"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <h4 className="font-semibold text-gray-900 truncate">
          {draft.name || 'Untitled Draft'}
        </h4>
        
        {draft.location && (
          <p className="text-sm text-gray-500 truncate">
            📍 {draft.location}
          </p>
        )}
        
        {displayDates.length > 0 && (
          <p className="text-sm text-gray-500">
            📅 {displayDates.join(', ')}
            {hasMoreDates && ` +${draft.dates.length - MAX_DATES_SHOWN} more`}
          </p>
        )}
        
        {truncatedDescription && (
          <p className="text-sm text-gray-400 mt-1 line-clamp-2">
            {truncatedDescription}
          </p>
        )}
      </div>

      {/* Delete Button */}
      <button
        onClick={handleDelete}
        className="flex-shrink-0 p-2 text-gray-400 hover:text-red-500 transition-colors"
        aria-label="Delete draft"
      >
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
          />
        </svg>
      </button>
    </div>
  );
};
